import React, { ErrorInfo } from 'react';
import { noop } from 'lodash';
import FallbackError from './FallbackError';
type ErrorBoundaryProps<T> = {
  children?: React.ReactNode;
  // Optional only because the is a default property
  ErrorComponent?: React.FC<T & {
    error: Error;
    reset?: () => void;
  }> | React.ComponentClass<T & {
    error: Error;
    reset?: () => void;
  }> | null;
  onError?: (error: Error) => void;
  /**
   * When this value changes, the error boundary resets its error state
   * and re-renders children. Useful for retry/recovery flows.
   */
  resetKey?: string | number;
};
type Props<T> = ErrorBoundaryProps<Omit<T, 'error' | 'reset'>> & Omit<T, keyof ErrorBoundaryProps<T>>;
export class ErrorBoundary<T extends Record<string, any>> extends React.Component<Props<T>, {
  error: Error | null;
  info: ErrorInfo | null;
}> {
  static defaultProps = {
    ErrorComponent: FallbackError,
    children: undefined,
    onError: noop,
    resetKey: undefined
  };
  constructor(props: Props<T>) {
    super(props);
    this.state = {
      error: null,
      info: null
    };
  }
  componentDidUpdate(prevProps: Props<T>) {
    const {
      error
    } = this.state;
    const {
      resetKey
    } = this.props;
    if (error !== null && prevProps.resetKey !== resetKey) {
      this.setState({
        error: null,
        info: null
      });
    }
  }
  componentDidCatch(error: Error, info: ErrorInfo) {
    const {
      onError
    } = this.props;
    if (onError) onError(error);
    // Display fallback UI
    this.setState({
      error,
      info
    });
  }
  reset = () => {
    this.setState({
      error: null,
      info: null
    });
  };
  render() {
    const {
      children,
      ErrorComponent,
      resetKey: _resetKey,
      ...rest
    } = this.props;
    const {
      error
    } = this.state;
    if (error !== null) {
      return ErrorComponent ? <ErrorComponent {...rest} error={error} reset={this.reset} /> : null;
    }
    return children;
  }
}